import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Schedule,
  Assignment,
  Build,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Add,
  Edit,
  Visibility,
  CalendarToday,
  Event,
  PriorityHigh,
} from '@mui/icons-material';
import dayjs from 'dayjs';

import { maintenanceService } from '../../services/maintenanceService';
import { useSiteContext } from '../../hooks/useSiteContext';
import HierarchicalOverdueTaskManager from '../../components/Maintenance/HierarchicalOverdueTaskManager';
import {
  MaintenanceDashboard as DashboardData,
  MaintenanceWorkOrder,
  MaintenanceWorkOrderSummary,
  MaintenanceSchedule,
  CreateMaintenanceWorkOrder,
  Priority,
  WorkOrderStatus,
} from '../../types/maintenance';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

// Type for calendar events
interface CalendarEvent {
  type: 'schedule' | 'workorder';
  title: string;
  equipment?: string;
  status?: string;
  priority?: string;
  isOverdue?: boolean;
  data: any;
}

// Calendar component for maintenance scheduling
const MaintenanceCalendar: React.FC<{
  schedules: any[];
  workOrders: any[];
  currentDate: Date;
  onDateChange: (date: Date) => void;
}> = ({ schedules, workOrders, currentDate, onDateChange }) => {
      const monthStart = dayjs(currentDate).startOf('month');
    const monthEnd = dayjs(currentDate).endOf('month');
    const monthDays = [];
    let current = monthStart;
    while (current.isBefore(monthEnd) || current.isSame(monthEnd, 'day')) {
      monthDays.push(current.toDate());
      current = current.add(1, 'day');
    }

  // Get events for a specific date
  const getEventsForDate = (date: Date): CalendarEvent[] => {
          const dateStr = dayjs(date).format('YYYY-MM-DD');
    const events: CalendarEvent[] = [];

    // Add overdue/upcoming schedules
    schedules.forEach(schedule => {
              if (schedule.next_due_date && dayjs(schedule.next_due_date).isSame(date, 'day')) {
        events.push({
          type: 'schedule',
          title: schedule.task_name,
          equipment: schedule.equipment_name,
          isOverdue: new Date(schedule.next_due_date) < new Date(),
          data: schedule
        });
      }
    });

    // Add work orders
    workOrders.forEach(workOrder => {
      if (workOrder.planned_start_date && dayjs(workOrder.planned_start_date).isSame(date, 'day')) {
        events.push({
          type: 'workorder',
          title: workOrder.work_order_number || 'Work Order',
          status: workOrder.status,
          priority: workOrder.priority,
          data: workOrder
        });
      }
    });

    return events;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {dayjs(currentDate).format('MMMM yyyy')}
          </Typography>
          <Box>
            <Button onClick={() => onDateChange(dayjs(currentDate).add(-30, 'day').toDate())}>Previous</Button>
            <Button onClick={() => onDateChange(new Date())}>Today</Button>
            <Button onClick={() => onDateChange(dayjs(currentDate).add(30, 'day').toDate())}>Next</Button>
          </Box>
        </Box>

        <Grid container spacing={1}>
          {/* Days of week header */}
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <Grid item xs={12/7} key={day}>
              <Typography variant="subtitle2" textAlign="center" sx={{ py: 1, fontWeight: 'bold' }}>
                {day}
              </Typography>
            </Grid>
          ))}

          {/* Calendar days */}
          {monthDays.map(date => {
            const events = getEventsForDate(date);
            const hasOverdue = events.some(e => e.type === 'schedule' && e.isOverdue);
            
            return (
              <Grid item xs={12/7} key={date.toString()}>
                <Card 
                  variant="outlined" 
                  sx={{ 
                    minHeight: 100,
                    backgroundColor: dayjs().isSame(date, 'day') ? 'primary.light' : 'inherit',
                    border: hasOverdue ? '2px solid red' : '1px solid grey.300',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'grey.50' }
                  }}
                  onClick={() => onDateChange(date)}
                >
                  <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                    <Typography variant="body2" fontWeight={dayjs().isSame(date, 'day') ? 'bold' : 'normal'}>
                      {dayjs(date).format('d')}
                    </Typography>
                    {events.map((event, idx) => (
                      <Tooltip key={idx} title={`${event.title} - ${event.equipment || event.status}`}>
                        <Chip
                          label={event.title.substring(0, 10) + (event.title.length > 10 ? '...' : '')}
                          size="small"
                          color={
                            event.type === 'schedule' ? (event.isOverdue ? 'error' : 'warning') :
                            event.status === 'completed' ? 'success' : 'info'
                          }
                          sx={{ fontSize: '0.6rem', height: 16, mb: 0.5, display: 'block' }}
                        />
                      </Tooltip>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </CardContent>
    </Card>
  );
};

const MaintenanceDashboard: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [workOrders, setWorkOrders] = useState<MaintenanceWorkOrder[]>([]);
  const [schedules, setSchedules] = useState<MaintenanceSchedule[]>([]);
  
  // Calendar state
  const [calendarDate, setCalendarDate] = useState(new Date());
  
  // Dialog states
  const [createWorkOrderOpen, setCreateWorkOrderOpen] = useState(false);
  const [workOrderForm, setWorkOrderForm] = useState<Partial<CreateMaintenanceWorkOrder>>({
    title: '',
    description: '',
    work_description: '',
    priority: Priority.NORMAL,
    status: WorkOrderStatus.PLANNED,
  });

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!currentSite.siteId) return;

      try {
        setLoading(true);
        setError(null);

        const [dashboard, workOrdersData, schedulesData] = await Promise.all([
          maintenanceService.dashboard.getDashboardData(currentSite.siteId.toString()),
          maintenanceService.workOrders.getWorkOrders({ site: currentSite.siteId.toString() }),
          maintenanceService.schedules.getSchedules({ site: currentSite.siteId.toString() }),
        ]);

        setDashboardData(dashboard);
        setWorkOrders(workOrdersData.results || []);
        setSchedules(schedulesData.results || []);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [currentSite.siteId]);

  // Handle work order actions
  const handleStartWorkOrder = async (id: number) => {
    try {
      await maintenanceService.workOrders.startWorkOrder(id);
      // Refresh work orders
      const workOrdersData = await maintenanceService.workOrders.getWorkOrders({ 
        site: currentSite.siteId!.toString() 
      });
      setWorkOrders(workOrdersData.results || []);
    } catch (err) {
      console.error('Failed to start work order:', err);
    }
  };

  const handleCompleteWorkOrder = async (id: number) => {
    try {
      await maintenanceService.workOrders.completeWorkOrder(id);
      // Refresh work orders
      const workOrdersData = await maintenanceService.workOrders.getWorkOrders({ 
        site: currentSite.siteId!.toString() 
      });
      setWorkOrders(workOrdersData.results || []);
    } catch (err) {
      console.error('Failed to complete work order:', err);
    }
  };

  const handleCreateWorkOrder = async () => {
    try {
      await maintenanceService.workOrders.createWorkOrder(workOrderForm as CreateMaintenanceWorkOrder);
      setCreateWorkOrderOpen(false);
      setWorkOrderForm({
        title: '',
        description: '',
        work_description: '',
        priority: Priority.NORMAL,
        status: WorkOrderStatus.PLANNED,
      });
      // Refresh work orders
      const workOrdersData = await maintenanceService.workOrders.getWorkOrders({ 
        site: currentSite.siteId!.toString() 
      });
      setWorkOrders(workOrdersData.results || []);
    } catch (err) {
      console.error('Failed to create work order:', err);
    }
  };

  const getStatusColor = (status: WorkOrderStatus) => {
    switch (status) {
      case WorkOrderStatus.COMPLETED: return 'success';
      case WorkOrderStatus.IN_PROGRESS: return 'warning';
      case WorkOrderStatus.PLANNED: return 'info';
      case WorkOrderStatus.CANCELLED: return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: Priority) => {
    switch (priority) {
      case Priority.CRITICAL: return 'error';
      case Priority.URGENT: return 'warning';
      case Priority.HIGH: return 'warning';
      case Priority.NORMAL: return 'primary';
      case Priority.LOW: return 'info';
      default: return 'default';
    }
  };

  if (!currentSite.siteId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Please select a site to view maintenance dashboard.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Maintenance Dashboard</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateWorkOrderOpen(true)}
        >
          Create Work Order
        </Button>
      </Box>

      {/* Summary Cards */}
      {dashboardData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <Assignment sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{dashboardData.total_work_orders || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Work Orders
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <Build sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{dashboardData.active_work_orders_count || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Work Orders
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ border: dashboardData.overdue_count > 0 ? '2px solid red' : 'inherit' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ position: 'relative', mr: 2 }}>
                  <Schedule sx={{ fontSize: 40, color: dashboardData.overdue_count > 0 ? 'error.main' : 'info.main' }} />
                  {dashboardData.overdue_count > 0 && (
                    <Badge
                      badgeContent={dashboardData.overdue_count}
                      color="error"
                      sx={{ position: 'absolute', top: -8, right: -8 }}
                    />
                  )}
                </Box>
                <Box>
                  <Typography variant="h6" color={dashboardData.overdue_count > 0 ? 'error.main' : 'inherit'}>
                    {dashboardData.overdue_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overdue Tasks
                  </Typography>
                  {dashboardData.due_this_week > 0 && (
                    <Typography variant="caption" color="warning.main">
                      +{dashboardData.due_this_week} due this week
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                <Box>
                  <Typography variant="h6">{dashboardData.completed_today || 0}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed Today
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)}>
          <Tab icon={<CalendarToday />} label="Calendar View" />
          <Tab icon={<PriorityHigh />} label="Overdue Tasks" />
          <Tab icon={<Assignment />} label="Active Work Orders" />
          <Tab icon={<Schedule />} label="Upcoming Tasks" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={currentTab} index={0}>
        {/* Calendar View */}
        <MaintenanceCalendar
          schedules={[...(dashboardData?.overdue_schedules || []), ...(dashboardData?.upcoming_schedules || [])]}
          workOrders={dashboardData?.active_work_orders || []}
          currentDate={calendarDate}
          onDateChange={setCalendarDate}
        />
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {/* Hierarchical Overdue Tasks */}
        <HierarchicalOverdueTaskManager
          siteName={currentSite.siteName}
          onTaskComplete={(taskId) => {
            console.log('Complete task:', taskId);
            // Reload dashboard data after task completion
            // TODO: Implement proper task completion API calls
          }}
          onAssignmentComplete={(taskId, assignmentId) => {
            console.log('Complete assignment:', taskId, assignmentId);
            // Reload dashboard data after assignment completion
            // TODO: Implement proper assignment completion API calls
          }}
        />
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {/* Active Work Orders */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Active Work Orders</Typography>
            <List>
              {workOrders.slice(0, 10).map((workOrder) => (
                <ListItem key={workOrder.id} divider>
                  <ListItemIcon>
                    <Assignment color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={workOrder.title || workOrder.work_description}
                    secondary={
                      <Box>
                        <Typography variant="body2">{workOrder.description || workOrder.work_description}</Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={workOrder.status}
                            color={getStatusColor(workOrder.status) as any}
                            size="small"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={workOrder.priority}
                            color={getPriorityColor(workOrder.priority) as any}
                            size="small"
                          />
                        </Box>
                      </Box>
                    }
                  />
                  <Box>
                    {workOrder.status === WorkOrderStatus.PLANNED && (
                      <IconButton
                        color="primary"
                        onClick={() => handleStartWorkOrder(workOrder.id)}
                        title="Start Work Order"
                      >
                        <PlayArrow />
                      </IconButton>
                    )}
                    {workOrder.status === WorkOrderStatus.IN_PROGRESS && (
                      <IconButton
                        color="success"
                        onClick={() => handleCompleteWorkOrder(workOrder.id)}
                        title="Complete Work Order"
                      >
                        <CheckCircle />
                      </IconButton>
                    )}
                    <IconButton color="default" title="View Details">
                      <Visibility />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
              {workOrders.length === 0 && (
                <ListItem>
                  <ListItemText primary="No active work orders" />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Upcoming Schedules</Typography>
            <List>
              {schedules.slice(0, 10).map((schedule) => (
                <ListItem key={schedule.id} divider>
                  <ListItemIcon>
                    <Schedule color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary={schedule.name || schedule.task_name}
                    secondary={
                      <Box>
                        <Typography variant="body2">{schedule.description || schedule.equipment_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Next due: {new Date(schedule.next_due_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
              {schedules.length === 0 && (
                <ListItem>
                  <ListItemText primary="No upcoming schedules" />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Recent Activity</Typography>
            <List>
              {dashboardData?.recent_completions?.slice(0, 10).map((workOrder) => (
                <ListItem key={workOrder.id} divider>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={workOrder.work_order_number}
                    secondary={`Completed recently`}
                  />
                </ListItem>
              ))}
              {(!dashboardData?.recent_completions || dashboardData.recent_completions.length === 0) && (
                <ListItem>
                  <ListItemText primary="No recent activity" />
                </ListItem>
              )}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {/* Upcoming Tasks */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Schedule sx={{ color: 'info.main', mr: 1 }} />
              <Typography variant="h6">
                Upcoming Maintenance Tasks
              </Typography>
            </Box>
            {dashboardData?.upcoming_schedules && dashboardData.upcoming_schedules.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Equipment</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Days Until Due</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.upcoming_schedules.map((schedule) => (
                      <TableRow key={schedule.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {schedule.task_name}
                          </Typography>
                        </TableCell>
                        <TableCell>{schedule.equipment_name}</TableCell>
                        <TableCell>
                          <Typography>
                            {schedule.next_due_date ? dayjs(schedule.next_due_date).format('MMM dd, yyyy') : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={`${schedule.days_until_due || 0} days`}
                            color={
                              (schedule.days_until_due || 0) <= 7 ? 'warning' :
                              (schedule.days_until_due || 0) <= 14 ? 'info' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label="Normal"
                            color="default"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button size="small" variant="outlined" color="primary">
                            Schedule Work Order
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No upcoming maintenance tasks in the next 2 weeks.</Alert>
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Create Work Order Dialog */}
      <Dialog open={createWorkOrderOpen} onClose={() => setCreateWorkOrderOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Work Order</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={workOrderForm.title || ''}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={workOrderForm.description || ''}
                onChange={(e) => setWorkOrderForm({ ...workOrderForm, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={workOrderForm.priority || Priority.NORMAL}
                  label="Priority"
                  onChange={(e) => setWorkOrderForm({ ...workOrderForm, priority: e.target.value as Priority })}
                >
                  <MenuItem value={Priority.LOW}>Low</MenuItem>
                  <MenuItem value={Priority.NORMAL}>Normal</MenuItem>
                  <MenuItem value={Priority.HIGH}>High</MenuItem>
                  <MenuItem value={Priority.URGENT}>Urgent</MenuItem>
                  <MenuItem value={Priority.CRITICAL}>Critical</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Scheduled Start"
                value={workOrderForm.scheduled_start || ''}
                onChange={(e) => setWorkOrderForm({ 
                  ...workOrderForm, 
                  scheduled_start: e.target.value
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateWorkOrderOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateWorkOrder} variant="contained">
            Create Work Order
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaintenanceDashboard; 